import { CHAT_BUILD_VERSION } from "@/lib/cache/chatBuildVersion";
import {
  enableChatWebPush,
  isChatWebPushSupported,
  readChatWebPushPref,
} from "@/lib/chatWebPush";

const CHAT_WEB_PUSH_SW_KEY = "chat_web_push_sw_build";

/**
 * Resubscribe si la préférence est on. Après un nouveau SW, iOS garde parfois
 * un endpoint mort — on force un nouvel abonnement une fois par build.
 */
export async function syncChatWebPushOnBoot(suiteUserId: string | null): Promise<void> {
  if (!suiteUserId || !readChatWebPushPref()) return;
  if (!isChatWebPushSupported()) return;
  if (Notification.permission !== "granted") return;

  let force = false;
  try {
    force = localStorage.getItem(CHAT_WEB_PUSH_SW_KEY) !== CHAT_BUILD_VERSION;
  } catch {
    force = true;
  }

  if (force && "serviceWorker" in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
    } catch {
      /* ignore */
    }
  }

  await enableChatWebPush(suiteUserId);
  try {
    localStorage.setItem(CHAT_WEB_PUSH_SW_KEY, CHAT_BUILD_VERSION);
  } catch {
    /* ignore */
  }
}

/**
 * Chat ouvert : couper les toasts message. iOS ne signale souvent pas
 * client.focused — heartbeat court + pagehide/freeze pour ne pas avaler les appels.
 */
export function installChatPushFocusGuard(): () => void {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return () => undefined;
  }

  const post = (focused: boolean) => {
    try {
      navigator.serviceWorker.controller?.postMessage({
        type: "CHAT_CLIENT_FOCUS",
        focused,
      });
    } catch {
      /* ignore */
    }
  };

  const dismissVisible = () => {
    post(true);
    void navigator.serviceWorker.ready
      .then((reg) => reg.getNotifications())
      .then((list) => {
        for (const n of list) n.close();
      })
      .catch(() => undefined);
  };

  const sync = () => {
    if (document.visibilityState === "visible") dismissVisible();
    else post(false);
  };

  const onHidden = () => post(false);

  sync();
  const id = window.setInterval(() => {
    if (document.visibilityState === "visible") post(true);
  }, 2000);
  document.addEventListener("visibilitychange", sync);
  window.addEventListener("focus", sync);
  window.addEventListener("pagehide", onHidden);
  window.addEventListener("blur", onHidden);
  document.addEventListener("freeze", onHidden);
  const onHub = (event: MessageEvent) => {
    const t = (event.data as { type?: string } | null)?.type;
    if (t === "AURELIA_CHAT_FRAME_HIDDEN" || t === "AURELIA_SUITE_LOGOUT") {
      post(false);
    }
  };
  window.addEventListener("message", onHub);
  return () => {
    window.clearInterval(id);
    document.removeEventListener("visibilitychange", sync);
    window.removeEventListener("focus", sync);
    window.removeEventListener("pagehide", onHidden);
    window.removeEventListener("blur", onHidden);
    document.removeEventListener("freeze", onHidden);
    window.removeEventListener("message", onHub);
    post(false);
  };
}
